import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";

import { fetchBlockData, fetchUserMeData } from "api/auth";
import { UserMe, UserProfile } from "api/authTypes";

import {
  clearTokenCookie,
  getTokenFromCookie,
  setTokenCookie,
} from "logic/cookieHandling";
import { Environment } from "libLegacy/api/models";

export type Block = {
  id: string;
  name: string;
  description: string;
  floors: Pavement[];
};

export type Pavement = {
  id: string;
  name: string;
  blockId: string;
  environments: Environment[];
};

export interface UserContextProps {
  token: string;
  setToken: React.Dispatch<React.SetStateAction<string>>;
  userData: UserMe | undefined;
  setUserData: React.Dispatch<React.SetStateAction<UserMe | undefined>>;
  hasProsumer: boolean;
  hasOnboarded: boolean;
  automationSpaces: UserMe["automationSpaces"];
  userProfile: UserProfile | undefined;
  currentSpace: UserMe["automationSpaces"][0] | undefined;
  setCurrentSpace: React.Dispatch<
    React.SetStateAction<UserMe["automationSpaces"][0] | undefined>
  >;
  currentEnvironment: Environment | undefined;
  setCurrentEnvironment: React.Dispatch<
    React.SetStateAction<Environment | undefined>
  >;
  availableBlocks: Block[];
  currentBlock: Block | undefined;
  setCurrentBlock: React.Dispatch<React.SetStateAction<Block | undefined>>;
  availablePavements: Pavement[];
  currentPavement: Pavement | undefined;
  setCurrentPavement: React.Dispatch<
    React.SetStateAction<Pavement | undefined>
  >;
  availableEnvironments: Environment[];
}

export const UserContext = createContext<UserContextProps>(null!);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string>(getTokenFromCookie());
  const [userData, setUserData] = useState<UserMe | undefined>(undefined);
  const [currentSpace, setCurrentSpace] =
    useState<UserContextProps["currentSpace"]>(undefined);

  const [currentEnvironment, setCurrentEnvironment] = useState<
    Environment | undefined
  >(undefined);
  const [availableBlocks, setAvailableBlocks] = useState<Block[]>([]);
  const [currentBlock, setCurrentBlock] = useState<Block | undefined>(
    undefined
  );
  const [availablePavements, setAvailablePavements] = useState<Pavement[]>([]);
  const [currentPavement, setCurrentPavement] = useState<Pavement | undefined>(
    undefined
  );
  const [availableEnvironments, setAvailableEnvironments] = useState<
    Environment[]
  >([]);

  const loadUserMeData = useCallback(async (token) => {
    try {
      const userData = await fetchUserMeData(token);
      setUserData(userData);
    } catch (error) {
      setUserData(undefined);
    }
  }, []);

  const loadBlockData = useCallback(async (token, currentSpaceId) => {
    try {
      const res = await fetchBlockData(token, currentSpaceId || "");

      setAvailableBlocks(res || []);
    } catch (error) {
      setAvailableBlocks([]);
    }
  }, []);

  useEffect(() => {
    // update buildingData every time currentSpace get updated, as long as it's valid
    if (token && currentSpace?.id) {
      loadBlockData(token, currentSpace.id);
    }
  }, [token, currentSpace, loadBlockData]);

  useEffect(() => {
    setAvailablePavements(currentBlock?.floors || []);
  }, [currentBlock]);

  useEffect(() => {
    setAvailableEnvironments(currentPavement?.environments || []);
  }, [currentPavement]);

  // on login
  useEffect(() => {
    if (token) {
      loadUserMeData(token);
    }
  }, [token, loadUserMeData]);

  // on logout
  useEffect(() => {
    if (!token) {
      setUserData(undefined);
      setCurrentSpace(undefined);
      setAvailableBlocks([]);
      setAvailablePavements([]);
      setAvailableEnvironments([]);
      setCurrentBlock(undefined);
      setCurrentPavement(undefined);
      setCurrentEnvironment(undefined);
    }
  }, [token]);

  const hasProsumer: boolean = useMemo(
    () =>
      !!(
        userData?.automationSpaces.length &&
        userData.automationSpaces.some((space) => space.isProsumer)
      ),
    [userData]
  );

  const hasOnboarded: boolean = useMemo(
    () => !!userData?.hasCompletedOnboarding,
    [userData]
  );

  const automationSpaces = useMemo(
    () => userData?.automationSpaces || [],
    [userData]
  );

  const userProfile = useMemo(
    () => userData?.data.item || undefined,
    [userData]
  );

  useEffect(() => {
    if (!currentSpace && automationSpaces[0]) {
      setCurrentSpace(automationSpaces[0]);
    }
  }, [automationSpaces, currentSpace]);

  // clear currentEnvironment on space change
  useEffect(() => {
    setCurrentEnvironment(undefined);
    setCurrentBlock(undefined);
    setCurrentPavement(undefined);
    setAvailableBlocks([]);
    setAvailablePavements([]);
    setAvailableEnvironments([]);
  }, [currentSpace]);

  return (
    <UserContext.Provider
      value={{
        token,
        setToken,
        userData,
        setUserData,
        hasProsumer,
        hasOnboarded,
        automationSpaces,
        currentSpace,
        setCurrentSpace,
        currentEnvironment,
        setCurrentEnvironment,
        availableBlocks,
        currentBlock,
        setCurrentBlock,
        availablePavements,
        currentPavement,
        setCurrentPavement,
        availableEnvironments,
        userProfile,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const {
    token,
    userData,
    automationSpaces,
    currentSpace,
    hasOnboarded,
    hasProsumer,
    currentEnvironment,
    availableBlocks,
    currentBlock,
    availablePavements,
    currentPavement,
    availableEnvironments,
    userProfile,
  } = useContext(UserContext);
  return {
    token,
    hasProsumer,
    automationSpaces,
    currentSpace,
    hasOnboarded,
    userData,
    currentEnvironment,
    availableBlocks,
    currentBlock,
    availablePavements,
    currentPavement,
    availableEnvironments,
    userProfile,
  };
};

export const useUserContextSetters = () => {
  const {
    setToken,
    setUserData,
    setCurrentSpace,
    setCurrentEnvironment,
    setCurrentBlock,
    setCurrentPavement,
  } = useContext(UserContext);
  return {
    setToken,
    setUserData,
    setCurrentSpace,
    setCurrentEnvironment,
    setCurrentBlock,
    setCurrentPavement,
  };
};

export const useToken = () => {
  const { token } = useContext(UserContext);
  return token;
};

export const useSetLoginToken = () => {
  const { setToken } = useContext(UserContext);

  return useCallback(
    (token: string) => {
      setTokenCookie(token);
      setToken(token);
    },
    [setToken]
  );
};

export const useLogout = () => {
  const { setToken } = useContext(UserContext);

  return useCallback(() => {
    clearTokenCookie();
    setToken("");
  }, [setToken]);
};
